import { Calendar, Edit2, Trash2, MoreVertical, User } from "lucide-react";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TaskCard = ({ task, onEdit, onDelete, onStatusChange, listView = false }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "bg-red-100 text-red-700 border-red-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-700 border-green-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "bg-gray-100 text-gray-700";
      case "In Progress":
        return "bg-blue-100 text-blue-700";
      case "Done":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const isOverdue = task.due_date && task.status !== "Done" && new Date(task.due_date) < new Date();

  const statuses = ["To Do", "In Progress", "Done"];

  return (
    <div
      data-testid={`task-card-${task.id}`}
      className={cn(
        "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md dark:hover:shadow-gray-900/50 transition-shadow",
        listView && "hover:border-gray-300 dark:hover:border-gray-600"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3
            className="text-base font-medium text-gray-900 dark:text-white mb-2 break-words"
            data-testid={`task-title-${task.id}`}
          >
            {task.title}
          </h3>

          {task.description && (
            <p
              className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 break-words"
              data-testid={`task-description-${task.id}`}
            >
              {task.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium border ${getPriorityColor(task.priority)}`}
              data-testid={`task-priority-${task.id}`}
            >
              {task.priority}
            </span>

            {listView && (
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(task.status)}`}
                data-testid={`task-status-${task.id}`}
              >
                {task.status}
              </span>
            )}

            {task.category && (
              <span
                className="text-xs px-2.5 py-1 rounded-full font-medium bg-purple-100 text-purple-700"
                data-testid={`task-category-${task.id}`}
              >
                {task.category}
              </span>
            )}

            {task.user_email && (
              <span
                className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-100 text-blue-700 flex items-center gap-1"
                data-testid={`task-user-${task.id}`}
                title={task.user_email}
              >
                <User className="w-3 h-3" />
                {task.user_name || task.user_email}
              </span>
            )}
          </div>

          {task.due_date && (
            <div
              className={cn(
                "flex items-center gap-1.5 text-xs",
                isOverdue ? "text-red-600" : "text-gray-500"
              )}
              data-testid={`task-due-date-${task.id}`}
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>{format(new Date(task.due_date), "MMM dd, yyyy")}</span>
              {isOverdue && <span className="font-medium">(Overdue)</span>}
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              data-testid={`task-menu-${task.id}`}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onEdit(task)}
              data-testid={`task-edit-${task.id}`}
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Move to</DropdownMenuLabel>
            {statuses.map((status) => (
              <DropdownMenuItem
                key={status}
                onClick={() => onStatusChange(task.id, status)}
                disabled={task.status === status}
                data-testid={`task-status-change-${task.id}-${status.toLowerCase().replace(" ", "-")}`}
              >
                {status}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(task.id)}
              className="text-red-600 focus:text-red-600"
              data-testid={`task-delete-${task.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TaskCard;
